import {
  CheckCircle2,
  CircleDashed,
  FileText,
  GitBranch,
  Sparkles,
  Trophy,
  UserPlus,
  Calendar,
  XCircle,
  type LucideIcon,
} from "lucide-react";

import { AllisonOrb } from "@/components/alia/allison-orb";
import { cn, relativeTime } from "@/lib/utils";
import type { TimelineEvent } from "@/types";

const EVENT_CONFIG: Record<
  TimelineEvent["type"],
  {
    icon: LucideIcon | "allison";
    accent: string;
    accentBg: string;
  }
> = {
  lead_captured: {
    icon: UserPlus,
    accent: "text-stage-nuevo",
    accentBg: "bg-stage-nuevo/15 border-stage-nuevo/30",
  },
  ai_qualified: {
    icon: "allison",
    accent: "text-brand",
    accentBg: "bg-brand/10 border-brand/30",
  },
  ai_responded: {
    icon: "allison",
    accent: "text-brand",
    accentBg: "bg-brand/10 border-brand/30",
  },
  stage_changed: {
    icon: GitBranch,
    accent: "text-muted-foreground",
    accentBg: "bg-surface-2 border-border",
  },
  proposal_sent: {
    icon: FileText,
    accent: "text-warning",
    accentBg: "bg-warning/10 border-warning/30",
  },
  appointment_booked: {
    icon: Calendar,
    accent: "text-stage-calificado",
    accentBg: "bg-stage-calificado/10 border-stage-calificado/30",
  },
  won: {
    icon: Trophy,
    accent: "text-success",
    accentBg: "bg-success/15 border-success/40",
  },
  lost: {
    icon: XCircle,
    accent: "text-muted-foreground",
    accentBg: "bg-surface-2 border-border",
  },
};

interface TimelineProps {
  events: TimelineEvent[];
}

export function Timeline({ events }: TimelineProps) {
  if (events.length === 0) {
    return (
      <div className="flex items-center gap-2 text-xs text-muted-foreground">
        <CircleDashed className="size-3.5" />
        Sin eventos aún
      </div>
    );
  }

  return (
    <ol className="relative space-y-2">
      {/* Vertical line */}
      <span
        aria-hidden
        className="absolute left-[15px] top-3 bottom-3 w-px bg-border"
      />

      {events.map((event) => (
        <TimelineItem key={event.id} event={event} />
      ))}
    </ol>
  );
}

function TimelineItem({ event }: { event: TimelineEvent }) {
  const config = EVENT_CONFIG[event.type];
  const Icon = config.icon;

  return (
    <li className="relative flex items-start gap-3 animate-fade-in">
      <div
        className={cn(
          "relative z-10 flex size-8 shrink-0 items-center justify-center rounded-full border",
          config.accentBg
        )}
      >
        {Icon === "allison" ? (
          <AllisonOrb size={16} />
        ) : (
          <Icon className={cn("size-3.5", config.accent)} />
        )}
      </div>

      <div className="min-w-0 flex-1 pt-1.5">
        <div className="flex items-baseline justify-between gap-2">
          <p className="text-[13px] font-medium text-foreground">
            {event.label}
          </p>
          <time className="shrink-0 text-[11px] text-muted-foreground tabular-nums">
            {relativeTime(event.createdAt)}
          </time>
        </div>
        {event.detail && (
          <p className="mt-0.5 text-[11px] text-muted-foreground">
            {event.detail}
          </p>
        )}
      </div>
    </li>
  );
}

export { CheckCircle2, Sparkles };
